import type {
	ClientPortfolioServicesCreateRequestDTO,
	ClientPortfolioServicesEditRequestDTO,
} from "web_myplann/Infrastructure/Api/Api";
import type { ServicesFormOutput } from "web_myplann/Portfolio/Forms/ServicesForm/servicesFormSchema";
import {
	getServicesRequestVariant,
	ServicesRequestVariant,
} from "web_myplann/Portfolio/Helpers/commodityServiceBranch";

const METERED_FIELDS = [
	"unitOfMeasurement",
	"pricePerUnit",
	"depositAmount",
] as const;

const FIXED_PRICE_FIELDS = ["fixedPriceByFrequency"] as const;

const FREQUENCY_AMOUNT_FIELDS = [
	"depositAmount",
	"fixedPriceByFrequency",
] as const;

type PriceField =
	| (typeof METERED_FIELDS)[number]
	| (typeof FIXED_PRICE_FIELDS)[number];

const getPriceFieldsForVariant = (
	variant: ServicesRequestVariant,
): readonly PriceField[] => {
	switch (variant) {
		case ServicesRequestVariant.Metered:
			return METERED_FIELDS;
		case ServicesRequestVariant.FixedPrice:
			return FIXED_PRICE_FIELDS;
		case ServicesRequestVariant.Untyped:
			return [];
	}
};

const omitForeignPriceFields = <TValues extends Partial<ServicesFormOutput>>(
	values: TValues,
	variant: ServicesRequestVariant,
) => {
	const allowed = getPriceFieldsForVariant(variant);
	const result: Record<string, unknown> = {};

	for (const [key, value] of Object.entries(values)) {
		const isPriceField = (
			[...METERED_FIELDS, ...FIXED_PRICE_FIELDS] as readonly string[]
		).includes(key);

		if (!isPriceField || (allowed as readonly string[]).includes(key)) {
			result[key] = value;
		}
	}

	return result;
};

export const mapServicesFormToCreateRequest = (
	values: ServicesFormOutput,
): ClientPortfolioServicesCreateRequestDTO => {
	const variant = getServicesRequestVariant(values.commodityService);

	return omitForeignPriceFields(
		values,
		variant,
	) as ClientPortfolioServicesCreateRequestDTO;
};

export const mapServicesFormToEditRequest = (
	changedValues: Partial<ServicesFormOutput>,
	{
		commodityService,
		paymentFrequency,
	}: Pick<ServicesFormOutput, "commodityService" | "paymentFrequency">,
): ClientPortfolioServicesEditRequestDTO => {
	const variant = getServicesRequestVariant(commodityService);
	const payload = omitForeignPriceFields(changedValues, variant);

	if (variant !== ServicesRequestVariant.Untyped) {
		payload.commodityService = commodityService;
	}

	const sendsFrequencyAmount = FREQUENCY_AMOUNT_FIELDS.some(
		(field) => field in payload,
	);

	if (sendsFrequencyAmount && !("paymentFrequency" in payload)) {
		payload.paymentFrequency = paymentFrequency;
	}

	return payload as ClientPortfolioServicesEditRequestDTO;
};
